import { useEffect, useState, useRef } from "react";
import Image1 from "../../../../assets/business/images/total-redemptions-img.png";
import Image2 from "../../../../assets/business/images/value-redeemed-img.png";
import { Link, NavLink } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TransactionGraphData } from "../../services/reports.services";

export default function Reports() {
  const [openPopupIndex, setOpenPopupIndex] = useState(null);
  const popupRef = useRef(null);
  const [dashboardData, setDashboardData] = useState({
    totalRedemptionCount: 0,
    valueRedeemed: 0,
    recentRedeemed: [],
    monthlyStats: [],
    currency: "EUR",
  });
  const [chartData, setChartData] = useState([]);
  const [filters, setFilters] = useState({ name: '', date: '' , rangeType : 'year' });

  // Helper function to get month name
  const getMonthName = (monthNumber) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months[monthNumber - 1];
  };

  // Helper function to handle Y-axis ticks for empty data
  const getYAxisTicks = (data, dataKey, defaultMax = 100) => {
    if (!data || data.length === 0) {
      // Return default ticks when no data
      return {
        ticks: [0, 20, 40, 60, 80, 100],
        max: 100,
      };
    }

    const maxValue = Math.max(...data.map((d) => d[dataKey]), 0);
    const step = Math.ceil(maxValue / 5) || 20; // Fallback to 20 if maxValue is 0
    const yMax = step * 5;
    const ticks = Array.from({ length: 6 }, (_, i) => i * step);

    return { ticks, max: yMax };
  };

  // Generate empty chart data for display when no data is available
  const getEmptyChartData = () => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const currentDate = new Date();
    const emptyData = [];

    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      emptyData.push({
        name: months[date.getMonth()],
        fullName: `${months[date.getMonth()]} ${date.getFullYear()}`,
        monthYear: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
        valueRedeemed: 0,
        redemptionCount: 0,
        month: date.getMonth() + 1,
        year: date.getFullYear(),
      });
    }

    return emptyData;
  };

  // Simplified: Backend already sends last 12 months, just format for charts
  const processMonthlyStats = (monthlyStats) => {
    if (!monthlyStats || !Array.isArray(monthlyStats)) return [];

    // Backend already sorted by year, month - just transform for chart display
    return monthlyStats.map((stat) => ({
      name: getMonthName(stat.month), // Only month name for X-axis
      fullName: `${getMonthName(stat.month)} ${stat.year}`, // Full name for tooltip
      monthYear: stat.monthKey,
      valueRedeemed: parseFloat(stat.valueRedeemed?.toFixed(2) || 0),
      redemptionCount: stat.redemptionCount || 0,
      month: stat.month,
      year: stat.year,
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setOpenPopupIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await TransactionGraphData({...filters});
        const data = response.data;
        console.log("Dashboard Data:", data);

        if (data) {
          setDashboardData(data);

          // Process chart data - backend already provides filtered last 12 months
          const processedChartData = processMonthlyStats(data.monthlyStats);
          setChartData(processedChartData);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard:", error);
      }
    };

    fetchData();
  }, []);

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const formattedDate = dataPoint.fullName;

      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="font-medium">{formattedDate}</p>
          {payload.map((entry, index) => (
            <div key={index} style={{ color: "#6A6A6A" }}>
              <span>
                {entry.name === "valueRedeemed" ? "Total Amount" : "Total Transaction Count"}:{entry.name === "valueRedeemed" ? ` £${entry.value}` : ` ${entry.value}`}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const currencySymbol = dashboardData.currency === "GBP" ? "£" : "$";

  const weeklyStats = dashboardData.weeklyStats || {
    thisWeek: { valueRedeemed: 0, redemptionCount: 0 },
    lastWeek: { valueRedeemed: 0, redemptionCount: 0 },
    growth: { valueRedeemed: 0, redemptionCount: 0 },
  };

  const valueGrowth = weeklyStats.growth.valueRedeemed;
  const countGrowth = weeklyStats.growth.redemptionCount;
  const currentWeekValue = weeklyStats.thisWeek.valueRedeemed;
  const currentWeekCount = weeklyStats.thisWeek.redemptionCount;

  // Get chart data for display (use empty data if no real data)
  const displayChartData = chartData.length > 0 ? chartData : getEmptyChartData();

  // Get Y-axis configurations
  const valueAxisConfig = getYAxisTicks(chartData, "valueRedeemed");
  const countAxisConfig = getYAxisTicks(chartData, "redemptionCount");

  return (
    <div className="main-content">
      <div className="deshboard-part">

      <div className="report-part">
  <ul className="nav custom-tab-buttons" role="tablist">
    <li className="nav-item">
      <NavLink
        to="/business/reports"
        className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
      >
        Transactions
      </NavLink>
    </li>
    <li className="nav-item">
      <NavLink
        to="/business/user-reports"
        className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
      >
        Customers
      </NavLink>
    </li>
    <li className="nav-item">
      <NavLink
        to="/business/offline-transaction-reports"
        className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
      >
        Offline Payments
      </NavLink>
    </li>
  </ul>
</div>


        <div class="card-box d-flex flex-wrap align-items-center gap-2 my-3">
                     <div class="search-bar">
                        <div class="search-input">
                           <input type="text" class="form-control" placeholder="Search"/>
                           <i class="fi fi-rs-search"></i>
                        </div>
                     </div>
                     <div class="dropdown custom-dropdown">
                        <button class="btn dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                        By Month <i class="fi fi-rr-angle-small-down"></i>
                        </button>
                        <ul class="dropdown-menu">
                           <li><a class="dropdown-item" href="#">Monthly</a></li>
                           <li><a class="dropdown-item" href="#">Yearly</a></li>
                           <li><button class="dropdown-item" data-bs-toggle="modal" data-bs-target="#exampleModal">Custom Range</button></li>
                        </ul>
                     </div>
                     <button class="btn btn-oring">Reset Filter</button>
                  
                  </div>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card-box-1">
              <div className="left-box-1">
                <p className="body-1">Total Transactions</p>
                <h3 className="h3-headline">{dashboardData.totalRedemptionCount}</h3>
              </div>
              <figure>
                <img src={Image1} alt="Total Redemptions" />
              </figure>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card-box-1">
              <div className="left-box-1">
                <p className="body-1">Total Transaction Amount</p>
                <h3 className="h3-headline">
                  {currencySymbol}
                  {dashboardData.valueRedeemed?.toFixed(2)}
                </h3>
              </div>
              <figure>
                <img src={Image2} alt="Value Redeemed" />
              </figure>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="row mt-4">
          <div className="graph-box">
            <h6 className="custem">Last 12 Months</h6>
            <div className="d-flex justify-content-between mb-3">
              {/* Value Redeemed Chart */}
              <div className="card-box w-100 d-flex flex-column">
                <h6 className="body-havey">Transaction Amount </h6>
                <p>
                  Total This Week: {currencySymbol}
                  {currentWeekValue?.toFixed(2)} &nbsp; | &nbsp; Growth:{" "}
                  <strong>
                    {valueGrowth > 0 ? "+" : ""}
                    {valueGrowth}%
                  </strong>
                </p>
                <div style={{ flex: 1, marginLeft: "-1rem", marginTop: "1rem" }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={displayChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" height={40} />
                      <YAxis ticks={valueAxisConfig.ticks} domain={[0, valueAxisConfig.max]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="valueRedeemed" stroke="#F0913A" strokeWidth={2} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Total Redemptions Chart */}
              <div className="card-box w-100 d-flex flex-column">
                <h6 className="body-havey">Transaction Count</h6>
                <p>
                  Total This Week: {currentWeekCount} &nbsp; | &nbsp; Growth:{" "}
                  <strong>
                    {countGrowth > 0 ? "+" : ""}
                    {countGrowth}%
                  </strong>
                </p>
                <div style={{ flex: 1, marginLeft: "-2.5rem", marginTop: "1rem" }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={displayChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" height={40} />
                      <YAxis ticks={countAxisConfig.ticks} domain={[0, countAxisConfig.max]} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="redemptionCount" stroke="#F0913A" strokeWidth={2} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Redeemed Table */}
        <div className="card-box mt-4">
          <div className="d-flex justify-content-between mb-3">
            <h6 className="custem">Recent Redeemed</h6>
            <Link to={"/business/transactions"} className="text-decoration-none custem">
              View All
            </Link>
          </div>
          <table className="table table-striped">
            <thead>
              <tr className="body-3">
                <th>Date</th>
                <th>Customer Email</th>
                <th>Offer Used</th>
                <th>Amount</th>
                <th>Type</th>
                <th>Status</th>
                <th>Reference ID</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.recentRedeemed.map((txn, idx) => (
                <tr key={idx} className="change-color">
                  <td>{new Date(txn.createdAt).toLocaleDateString()}</td>
                  <td>{txn.user?.email || txn.userEmail || (txn.userName?.includes("@") ? txn.userName : null) || txn.userName || "N/A"}</td>
                  <td>{txn.offer_id ? txn.offer.name : `${txn.offerPercentage}%`}</td>
                  <td>
                    {currencySymbol}
                    {txn.netPay}
                  </td>
                  <td>{txn.paymentMethod || "N/A"}</td>
                  <td>
                    <span className="badge">{txn.status}</span>
                  </td>
                  <td>{txn.transactionID || "N/A"}</td>
                  <td className="popup">
                    <button className="btn btn-sm toggle-popup" onClick={() => setOpenPopupIndex(idx)}>
                      <i className="fi fi-rr-menu-dots-vertical" />
                    </button>

                    {openPopupIndex === idx && (
                      <div className="popup-box z-50" ref={popupRef}>
                        <ul>
                          <li>
                            <i className="fi fi-rr-eye" />
                            <Link to={`/business/transactions/${txn._id || txn.transactionId}`}>View Details</Link>
                          </li>
                    
                        </ul>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {dashboardData.recentRedeemed.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div class="modal fade" id="exampleModal" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
                     <div class="modal-dialog">
                        <div class="modal-content">
                           <div class="modal-header">
                              <h1 class="modal-title" id="exampleModalLabel">Filter by Custom Range</h1>
                              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"><i class="fi fi-rr-cross-circle"></i></button>
                           </div>
                           <div class="modal-body">
                              <div class="row">
                                 <div class="col-md-6">
                                    <label class="form-label subtitle-1">From</label>
                                    <input type="date" class="form-control" placeholder="Select date"/>
                                 </div>
                                 <div class="col-md-6">
                                    <label class="form-label subtitle-1">To</label> 
                                    <input type="date" placeholder="Date" class="form-control w-100"/>
                                 </div>
                              </div>
                           </div>
                           <div class="modal-footer">
                              <button type="button" class="btn btn-oring">Continue</button>
                           </div>
                        </div>
                     </div>
                  </div>
    </div>
  );
}
